import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LevelSelector from './components/LevelSelector';
import VerseDisplay from './components/VerseDisplay';
import NextVerses from './components/NextVerses';
import RangeSelector from './components/RangeSelector';
import RecitationAnswer from './components/RecitationAnswer';
import RecitationHistory from './components/RecitationHistory';
import QuranAPI, { RANGE_LIMITS } from './services/quranAPI';
import {
  clearHistory,
  loadHistory,
  recordQuestionResult,
  recordVerseAttempt,
  startExam as recordExamStart,
} from './services/historyStorage';
import './styles/App.css';

// Filtres de révision disponibles
const SELECTION_TYPES = [
  { id: 'level', label: 'Niveau', unit: 'hizb', unitPlural: 'hizbs' },
  { id: 'juz', label: 'Juz', unit: 'juz', unitPlural: 'juz' },
  { id: 'page', label: 'Pages', unit: 'page', unitPlural: 'pages' },
  { id: 'surah', label: 'Sourates', unit: 'sourate', unitPlural: 'sourates' }
];

// Libellé lisible de l'unité testée par une question
const UNIT_LABELS = {
  hizb: 'Hizb',
  juz: 'Juz',
  page: 'Page',
  surah: 'Sourate'
};

// Nombres de questions proposés en mode examen
const QUESTION_COUNTS = [3, 5, 7, 10, 15, 20];

// Nombre de versets à réciter pour que l'IA valide une question
const AI_VERSE_TARGETS = [1, 2, 3, 5];

// Plage par défaut appliquée lors du changement de filtre
const DEFAULT_RANGES = {
  juz: { start: 1, end: RANGE_LIMITS.juz },
  page: { start: 1, end: RANGE_LIMITS.page },
  surah: { start: 1, end: RANGE_LIMITS.surah }
};

const TRANSLATION_EDITION = 'fr.hamidullah';
const RECITATION_AI_HOSTNAME = 'quran.injaz.fr';

// Délai avant l'enchaînement automatique après un silence prolongé : laisse le
// temps de lire le verdict de la question qu'on quitte.
const AUTO_ADVANCE_DELAY_MS = 2500;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);

// En production, les fonctions IA restent réservées à quran.injaz.fr. En
// développement local elles sont activées, sinon il est impossible de tester
// la récitation sans déployer. Le flag explicite couvre les cas particuliers
// (aperçu d'un build, machine distante).
export const isRecitationAIAllowed = () =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENABLE_RECITATION_AI === 'true';

export const isRecitationAIHost = (hostname, allowLocal = false) =>
  hostname === RECITATION_AI_HOSTNAME || (allowLocal && LOCAL_HOSTNAMES.has(hostname));

// Formate un ratio sans décimale inutile (2 / 2,5)
const formatRatio = (value) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');

function App() {
  const canUseRecitationAI =
    typeof window !== 'undefined' &&
    isRecitationAIHost(window.location.hostname, isRecitationAIAllowed());

  // Historique local (localStorage) : erreurs par verset et examens passés.
  const [history, setHistory] = useState(() => loadHistory());
  const examIdRef = useRef(null);

  // Minuterie d'enchaînement automatique après silence prolongé.
  const autoAdvanceRef = useRef(null);
  const cancelAutoAdvance = useCallback(() => {
    if (autoAdvanceRef.current !== null) {
      window.clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  }, []);
  // Une minuterie encore armée au démontage déclencherait une navigation sur un
  // composant disparu.
  useEffect(() => cancelAutoAdvance, [cancelAutoAdvance]);

  // Filtre actif : 'level' (hizbs cumulés), 'juz', 'page' ou 'surah'
  const [selectionType, setSelectionType] = useState('level');

  // Niveau sélectionné (1, 2, 5, 10, 15, 20, 30, 60 hizb)
  const [selectedLevel, setSelectedLevel] = useState(null);

  // Bornes de la plage pour les filtres juz / page / sourate
  const [startRange, setStartRange] = useState(1);
  const [endRange, setEndRange] = useState(RANGE_LIMITS.page);

  // Sens de révision et mode examen
  const [reverseMode, setReverseMode] = useState(false);
  const [examMode, setExamMode] = useState(false);

  // Nombre de questions de l'examen, commun à tous les filtres
  const [questionCount, setQuestionCount] = useState(5);

  // Nombre de versets récités exigé par l'IA pour valider une question
  const [aiVersesTarget, setAiVersesTarget] = useState(3);

  // Verset affiché et déroulé de l'examen
  const [currentVerse, setCurrentVerse] = useState(null);
  const [examVerses, setExamVerses] = useState([]);
  const [currentExamIndex, setCurrentExamIndex] = useState(0);
  const [questionResults, setQuestionResults] = useState({});
  const [recitationProgress, setRecitationProgress] = useState(null);
  const [recitationMasked, setRecitationMasked] = useState(false);

  // Versets de réponse
  const [nextVerses, setNextVerses] = useState([]);
  const [showNextVerses, setShowNextVerses] = useState(false);

  // Chargements, erreurs et informations non bloquantes
  const [isLoading, setIsLoading] = useState(false);
  const [loadingNextVerses, setLoadingNextVerses] = useState(false);
  const [loadingExam, setLoadingExam] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const activeType = SELECTION_TYPES.find((type) => type.id === selectionType);
  const isRangeFilter = selectionType !== 'level';
  const isBusy = isLoading || loadingExam;
  const canGenerate = isRangeFilter ? Boolean(startRange && endRange) : Boolean(selectedLevel);

  // Nombre d'unités couvertes par le filtre courant (hizbs, juz, pages ou sourates)
  const rangeSize = useMemo(() => {
    if (!isRangeFilter) {
      return selectedLevel || 0;
    }
    const start = Number(startRange) || 1;
    const end = Number(endRange) || start;
    return Math.abs(end - start) + 1;
  }, [isRangeFilter, selectedLevel, startRange, endRange]);

  // Explique la répartition réellement appliquée par l'algorithme
  const distributionHint = useMemo(() => {
    if (!examMode || !rangeSize) {
      return null;
    }
    const { unit, unitPlural } = activeType;
    if (questionCount <= rangeSize) {
      const step = rangeSize / questionCount;
      return `${questionCount} questions couvrant ${rangeSize} ${unitPlural} : 1 question par tranche de ${formatRatio(step)} ${step < 2 ? unit : unitPlural}.`;
    }
    return `${questionCount} questions sur ${rangeSize} ${unitPlural} : environ ${formatRatio(questionCount / rangeSize)} questions par ${unit}.`;
  }, [examMode, questionCount, rangeSize, activeType]);

  // Unité du Coran testée par la question affichée (Hizb 12, Page 45, ...)
  const examScopeLabel = useMemo(() => {
    const info = currentVerse?.examInfo;
    if (!examMode || !info || info.selectedValue === undefined) {
      return null;
    }
    return `${UNIT_LABELS[info.selectionType] || ''} ${info.selectedValue}`.trim();
  }, [currentVerse, examMode]);

  // Remet la session (verset courant, réponses, examen) à zéro
  const resetSession = useCallback(() => {
    setCurrentVerse(null);
    setExamVerses([]);
    setCurrentExamIndex(0);
    setQuestionResults({});
    setRecitationProgress(null);
    setRecitationMasked(false);
    setNextVerses([]);
    setShowNextVerses(false);
    setError(null);
    setNotice(null);
  }, []);

  // Change de filtre et applique la plage par défaut correspondante
  const handleSelectionTypeChange = useCallback((type) => {
    setSelectionType(type);
    if (DEFAULT_RANGES[type]) {
      setStartRange(DEFAULT_RANGES[type].start);
      setEndRange(DEFAULT_RANGES[type].end);
    }
    resetSession();
  }, [resetSession]);

  // Génère un verset unique (mode libre)
  const generateRandomVerse = useCallback(async () => {
    try {
      setIsLoading(true);
      resetSession();

      const verse = isRangeFilter
        ? await QuranAPI.getRandomVerseByRange(selectionType, startRange, endRange, TRANSLATION_EDITION, reverseMode)
        : await QuranAPI.getRandomVerseByLevel(selectedLevel, TRANSLATION_EDITION, reverseMode);

      setCurrentVerse(verse);
    } catch (err) {
      console.error('Erreur API:', err);
      setError('Erreur lors de la récupération du verset. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  }, [isRangeFilter, resetSession, selectionType, startRange, endRange, reverseMode, selectedLevel]);

  // Génère un examen dont les questions sont réparties sur toute la plage
  const startExam = useCallback(async () => {
    try {
      setLoadingExam(true);
      resetSession();

      const verses = isRangeFilter
        ? await QuranAPI.getExamVersesByRange(selectionType, startRange, endRange, TRANSLATION_EDITION, reverseMode, questionCount)
        : await QuranAPI.getExamVerses(selectedLevel, TRANSLATION_EDITION, reverseMode, questionCount);

      if (!verses || verses.length === 0) {
        setError("Impossible de générer les questions de l'examen. Élargissez la plage puis réessayez.");
        return;
      }

      setExamVerses(verses);
      setCurrentVerse(verses[0]);
      setCurrentExamIndex(0);

      // Ouvre une entrée d'historique : les résultats des questions viendront
      // s'y rattacher au fur et à mesure.
      const scope = isRangeFilter
        ? `${activeType.label} ${startRange}–${endRange}`
        : `Niveau ${selectedLevel} hizb${selectedLevel > 1 ? 's' : ''}`;
      examIdRef.current = `exam-${Date.now()}`;
      setHistory(recordExamStart({
        id: examIdRef.current,
        scope,
        questionCount: verses.length,
        versesTarget: aiVersesTarget,
      }));

      if (verses.length < questionCount) {
        setNotice(`${verses.length} questions générées sur ${questionCount} demandées : la plage sélectionnée est trop étroite.`);
      }
    } catch (err) {
      console.error('Erreur API:', err);
      setError("Erreur lors de la génération de l'examen. Veuillez réessayer.");
    } finally {
      setLoadingExam(false);
    }
  }, [activeType, aiVersesTarget, isRangeFilter, resetSession, selectionType, startRange, endRange, reverseMode, questionCount, selectedLevel]);

  // Point d'entrée du bouton principal
  const generateVerse = useCallback(() => {
    if (!canGenerate) return;
    if (examMode) {
      startExam();
    } else {
      generateRandomVerse();
    }
  }, [canGenerate, examMode, startExam, generateRandomVerse]);

  // Navigation directe vers une question de l'examen
  const goToExamQuestion = useCallback((index) => {
    if (index < 0 || index >= examVerses.length) return;
    // Une navigation manuelle annule un enchaînement automatique en attente.
    cancelAutoAdvance();
    setShowNextVerses(false);
    setNextVerses([]);
    setRecitationProgress(null);
    setRecitationMasked(false);
    setCurrentExamIndex(index);
    setCurrentVerse(examVerses[index]);
    setError(null);
    setNotice(null);
  }, [cancelAutoAdvance, examVerses]);

  // Enregistre le résultat d'une question (passed / toReview)
  const markQuestionResult = (result) => {
    setQuestionResults((previous) => ({ ...previous, [currentExamIndex]: result }));
  };

  const handleRecitationProgress = useCallback((payload) => {
    if (payload.type === 'started') {
      setRecitationProgress(null);
      setRecitationMasked(false);
      return;
    }
    if (payload.type !== 'result') return;

    setRecitationProgress(payload);
    if (payload.transitioned) {
      setRecitationMasked(true);
    }
    // Chaque ayah terminée est archivée localement : `completed_ayah` n'apparaît
    // que dans un seul payload, il n'y a donc pas de double comptage.
    const completed = payload.completed_ayah;
    if (completed?.reference) {
      setHistory(recordVerseAttempt({
        surah: completed.reference.surah,
        ayah: completed.reference.ayah,
        correct: completed.correct_count,
        errors: completed.error_count,
      }));
    }
  }, []);

  const handleRecitationCompleted = useCallback((summary) => {
    if (!examMode || examVerses.length === 0) return;

    const result = summary.passed ? 'passed' : 'toReview';
    setQuestionResults((previous) => ({ ...previous, [currentExamIndex]: result }));
    if (examIdRef.current) {
      setHistory(recordQuestionResult(examIdRef.current, result));
    }

    // Silence prolongé : le serveur a clos la session, on enchaîne sans action
    // de l'utilisateur — il n'est plus devant l'écran ou a renoncé.
    if (summary.stopReason === 'silence') {
      const nextIndex = currentExamIndex + 1;
      if (nextIndex < examVerses.length) {
        setNotice(
          `Silence prolongé : question ${currentExamIndex + 1} classée à revoir, passage à la question ${nextIndex + 1}…`
        );
        autoAdvanceRef.current = window.setTimeout(
          () => goToExamQuestion(nextIndex),
          AUTO_ADVANCE_DELAY_MS
        );
      } else {
        setNotice('Silence prolongé : dernière question de l’examen, aucune suivante.');
      }
      return;
    }

    setNotice(
      summary.passed
        ? `Réponse vocale validée : ${summary.completedCount} verset${summary.completedCount > 1 ? 's' : ''} récité${summary.completedCount > 1 ? 's' : ''} (${Math.round(summary.accuracy * 100)}% de précision). Passez à la question suivante manuellement.`
        : summary.reachedTarget
          ? `Réponse vocale terminée avec ${Math.round(summary.accuracy * 100)}% de précision : question à revoir. Changez de question manuellement.`
          : `Réponse vocale interrompue à ${summary.completedCount} verset${summary.completedCount > 1 ? 's' : ''} sur ${aiVersesTarget} : question à revoir.`
    );
  }, [aiVersesTarget, currentExamIndex, examMode, examVerses.length, goToExamQuestion]);

  // Affiche les 20 versets suivants (la réponse)
  const showNextVersesHandler = useCallback(async () => {
    if (!currentVerse) return;

    try {
      setLoadingNextVerses(true);
      setError(null);
      setNotice(null);
      setNextVerses([]);

      const verses = await QuranAPI.getConsecutiveVerses(currentVerse.surah, currentVerse.numberInSurah + 1, 20);

      if (verses && verses.length > 0) {
        setNextVerses(verses);
        setShowNextVerses(true);
        if (verses.length < 20) {
          setNotice(`${verses.length} versets récupérés : la fin du Coran a été atteinte.`);
        }
      } else {
        setError("Aucun verset suivant n'a pu être récupéré. Veuillez réessayer.");
      }
    } catch (err) {
      console.error('Erreur API:', err);
      setError('Erreur lors de la récupération des versets suivants. Veuillez réessayer.');
    } finally {
      setLoadingNextVerses(false);
    }
  }, [currentVerse]);

  // Raccourcis clavier en mode examen : ← → pour naviguer, Espace pour révéler
  useEffect(() => {
    if (!examMode || examVerses.length === 0) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const tagName = event.target.tagName;
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(tagName)) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToExamQuestion(currentExamIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToExamQuestion(currentExamIndex - 1);
      } else if (event.key === ' ' && !showNextVerses && !loadingNextVerses) {
        event.preventDefault();
        showNextVersesHandler();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [examMode, examVerses.length, currentExamIndex, showNextVerses, loadingNextVerses, goToExamQuestion, showNextVersesHandler]);

  const evaluatedCount = Object.keys(questionResults).length;
  const passedCount = useMemo(() => Object.values(questionResults).filter((r) => r === 'passed').length, [questionResults]);
  const toReviewCount = useMemo(() => Object.values(questionResults).filter((r) => r === 'toReview').length, [questionResults]);
  const examActive = examMode && examVerses.length > 0;
  const examCompleted = examActive && evaluatedCount === examVerses.length;

  // Glisser gauche/droite sur mobile pour naviguer entre les questions
  const touchStartX = useRef(null);

  const handleTouchStart = useCallback((event) => {
    touchStartX.current = event.changedTouches[0].screenX;
  }, []);

  const handleTouchEnd = useCallback((event) => {
    if (!examActive || touchStartX.current === null) return;
    const endX = event.changedTouches[0].screenX;
    const diff = touchStartX.current - endX;
    if (Math.abs(diff) < 50) return;
    if (diff > 0) {
      goToExamQuestion(currentExamIndex + 1);
    } else {
      goToExamQuestion(currentExamIndex - 1);
    }
    touchStartX.current = null;
  }, [examActive, currentExamIndex, goToExamQuestion]);

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">Révision du Coran</h1>
        <p className="app-subtitle">Ciblez une plage précise, révisez librement ou en mode examen</p>
      </header>

      <main className="app-main" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <section className="panel">
          <div className="panel-head">
            <span className="panel-step">1</span>
            <h2 className="panel-title">Périmètre de révision</h2>
          </div>

          <div className="range-type-buttons" role="tablist" aria-label="Type de plage de révision">
            {SELECTION_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                role="tab"
                aria-selected={selectionType === type.id}
                className={`type-button ${selectionType === type.id ? 'active' : ''}`}
                onClick={() => handleSelectionTypeChange(type.id)}
              >
                {type.label}
              </button>
            ))}
          </div>

          {isRangeFilter ? (
            <RangeSelector
              rangeType={selectionType}
              startRange={startRange}
              endRange={endRange}
              onStartRangeChange={setStartRange}
              onEndRangeChange={setEndRange}
            />
          ) : (
            <LevelSelector
              selectedLevel={selectedLevel}
              onLevelChange={(level) => {
                setSelectedLevel(level);
                resetSession();
              }}
            />
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <span className="panel-step">2</span>
            <h2 className="panel-title">Options</h2>
          </div>

          <div className="options-grid">
            <div className="option-card">
              <div className="option-card-head">
                <span className="option-title">Sens de révision</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={reverseMode}
                    onChange={() => setReverseMode(!reverseMode)}
                    aria-label="Inverser le sens de révision"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="option-value">
                {reverseMode ? 'An-Nas → Al-Fatiha' : 'Al-Fatiha → An-Nas'}
              </p>
            </div>

            <div className="option-card">
              <div className="option-card-head">
                <span className="option-title">Mode examen</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={examMode}
                    onChange={() => {
                      setExamMode(!examMode);
                      resetSession();
                    }}
                    aria-label="Activer le mode examen"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="option-value">
                {examMode ? 'Série de questions réparties sur la plage' : 'Tirage libre, un verset à la fois'}
              </p>
            </div>

            {examMode && (
              <div className="option-card option-card-wide">
                <span className="option-title">Nombre de questions</span>
                <div className="count-chips" role="group" aria-label="Nombre de questions">
                  {QUESTION_COUNTS.map((count) => (
                    <button
                      key={count}
                      type="button"
                      className={`count-chip ${questionCount === count ? 'active' : ''}`}
                      onClick={() => setQuestionCount(count)}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {examMode && canUseRecitationAI && (
              <div className="option-card option-card-wide">
                <span className="option-title">Objectif IA : versets à réciter par question</span>
                <div className="count-chips" role="group" aria-label="Versets à réciter pour valider une question">
                  {AI_VERSE_TARGETS.map((target) => (
                    <button
                      key={target}
                      type="button"
                      className={`count-chip ${aiVersesTarget === target ? 'active' : ''}`}
                      onClick={() => setAiVersesTarget(target)}
                    >
                      {target}
                    </button>
                  ))}
                </div>
                <p className="option-hint">
                  L'IA valide la question après {aiVersesTarget} verset{aiVersesTarget > 1 ? 's' : ''} récité{aiVersesTarget > 1 ? 's' : ''} correctement.
                </p>
              </div>
            )}
          </div>

          {distributionHint && (
            <p className="distribution-hint">{distributionHint}</p>
          )}
        </section>

        {!examActive && (
          <div className="generate-section">
            <button
              className="generate-button"
              onClick={generateVerse}
              disabled={!canGenerate || isBusy}
            >
              {isBusy ? 'Chargement…' : examMode ? "Démarrer l'examen" : currentVerse ? 'Générer un autre verset' : 'Générer un verset'}
            </button>
            {!canGenerate && (
              <p className="generate-hint">
                {isRangeFilter ? 'Renseignez une plage valide pour commencer.' : 'Choisissez un niveau pour commencer.'}
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="app-message error-message" role="alert">{error}</div>
        )}

        {notice && (
          <div className="app-message notice-message">{notice}</div>
        )}

        {examActive && (
          <section className="exam-bar">
            <div className="exam-bar-head">
              <span className="exam-counter">
                Question {currentExamIndex + 1} / {examVerses.length}
              </span>
              <div className="exam-bar-side">
                {examScopeLabel && <span className="exam-scope">{examScopeLabel}</span>}
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={startExam}
                  disabled={isBusy}
                  title="Générer une nouvelle série de questions"
                >
                  ↺ Nouvel examen
                </button>
              </div>
            </div>

            <div className="exam-bar-score">
              <span className="score-pill score-passed">{passedCount} réussi{passedCount !== 1 ? 's' : ''}</span>
              <span className="score-pill score-to-review">{toReviewCount} à revoir</span>
              <span className="score-pill score-remaining">{examVerses.length - evaluatedCount} restante{examVerses.length - evaluatedCount !== 1 ? 's' : ''}</span>
            </div>

            <div className="exam-progress-track">
              <div
                className="exam-progress-fill"
                style={{ width: `${((currentExamIndex + 1) / examVerses.length) * 100}%` }}
              />
            </div>

            <div className="exam-dots">
              {examVerses.map((verse, index) => (
                <button
                  key={`dot-${verse.surah}-${verse.numberInSurah}-${index}`}
                  type="button"
                  className={`exam-dot${index === currentExamIndex ? ' current' : ''}${questionResults[index] === 'passed' ? ' passed' : ''}${questionResults[index] === 'toReview' ? ' to-review' : ''}`}
                  onClick={() => goToExamQuestion(index)}
                  aria-label={`Aller à la question ${index + 1}`}
                  title={`Question ${index + 1} — Sourate ${verse.surah}, verset ${verse.numberInSurah}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </section>
        )}

        <VerseDisplay
          verse={currentVerse}
          isLoading={isBusy}
          examInfo={examMode && currentVerse?.examInfo ? currentVerse.examInfo : null}
          recitationProgress={recitationProgress}
          recitationMasked={recitationMasked}
        />

        {currentVerse && canUseRecitationAI && (
          <RecitationAnswer
            verse={currentVerse}
            versesTarget={aiVersesTarget}
            startAfterVerse={examMode}
              onProgress={handleRecitationProgress}
            onCompleted={handleRecitationCompleted}
          />
        )}

        {currentVerse && (
          <div className="response-button-container">
            <button
              type="button"
              className="btn btn-primary"
              onClick={showNextVersesHandler}
              disabled={loadingNextVerses}
            >
              {loadingNextVerses ? 'Chargement…' : showNextVerses ? 'Recharger la réponse' : 'Voir la réponse'}
            </button>

            {examActive && showNextVerses && (
              <div className="exam-evaluation">
                <span className="evaluation-label">Évaluation :</span>
                <div className="evaluation-buttons">
                  <button
                    type="button"
                    className={`btn ${questionResults[currentExamIndex] === 'passed' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => markQuestionResult('passed')}
                  >
                    Réussi
                  </button>
                  <button
                    type="button"
                    className={`btn ${questionResults[currentExamIndex] === 'toReview' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => markQuestionResult('toReview')}
                  >
                    À revoir
                  </button>
                </div>
              </div>
            )}

            {examActive && examVerses.length > 1 && (
              <div className="exam-navigation-buttons">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => goToExamQuestion(currentExamIndex - 1)}
                  disabled={currentExamIndex === 0}
                >
                  ← Précédente
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => goToExamQuestion(currentExamIndex + 1)}
                  disabled={currentExamIndex >= examVerses.length - 1}
                >
                  Suivante →
                </button>
              </div>
            )}

            {examActive && (
              <p className="exam-shortcuts">Raccourcis : ← → pour naviguer, Espace pour révéler</p>
            )}
          </div>
        )}

        {examCompleted && (
          <section className="exam-recap">
            <h3 className="exam-recap-title">Examen terminé</h3>
            <div className="exam-final-score">
              <div className="final-score-line">
                <span className="final-score-label">Score final</span>
                <span className="final-score-value">{passedCount} / {examVerses.length}</span>
              </div>
              <span className="final-score-detail">{toReviewCount} à revoir</span>
            </div>
            <p className="exam-recap-subtitle">
              Cliquez sur une ligne pour revoir une question.
            </p>
            <ul className="exam-recap-list">
              {examVerses.map((verse, index) => (
                <li key={`recap-${verse.surah}-${verse.numberInSurah}-${index}`}>
                  <button type="button" className="recap-item" onClick={() => goToExamQuestion(index)}>
                    <span className="recap-index">{index + 1}</span>
                    <span className="recap-reference">Sourate {verse.surah}, verset {verse.numberInSurah}</span>
                    <span className="recap-scope">
                      {UNIT_LABELS[verse.examInfo?.selectionType] || ''} {verse.examInfo?.selectedValue}
                    </span>
                    <span className={`recap-status ${questionResults[index] === 'passed' ? 'status-passed' : questionResults[index] === 'toReview' ? 'status-to-review' : 'status-none'}`}>
                      {questionResults[index] === 'passed' ? 'Réussi' : questionResults[index] === 'toReview' ? 'À revoir' : '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn-primary" onClick={startExam} disabled={isBusy}>
              Relancer un examen
            </button>
          </section>
        )}

        <NextVerses
          nextVerses={nextVerses}
          isVisible={showNextVerses}
          onClose={() => setShowNextVerses(false)}
          isLoading={loadingNextVerses}
        />

        <RecitationHistory
          history={history}
          onClear={() => {
            examIdRef.current = null;
            setHistory(clearHistory());
          }}
        />
      </main>

      <footer className="footer">
        <p>Les questions sont réparties automatiquement sur toute la plage choisie afin de couvrir l'ensemble du programme.</p>
      </footer>
    </div>
  );
}

export default App;
